// src/pages/RobotsGen.tsx
import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Space,
  Divider,
  Row,
  Col,
  message,
} from "antd";
import {
  CopyOutlined,
  DownloadOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;
const { Option } = Select;

interface Rule {
  agent: string;
  allow: string;
  disallow: string;
}

const RobotsGen: React.FC = () => {
  const [sitemap, setSitemap] = useState("");
  const [rules, setRules] = useState<Rule[]>([
    { agent: "*", allow: "", disallow: "/admin/" },
  ]);
  const [output, setOutput] = useState("");

  // Generate the file content whenever inputs change
  useEffect(() => {
    let content = "";

    rules.forEach((rule) => {
      content += `User-agent: ${rule.agent}\n`;
      if (rule.disallow) {
        rule.disallow.split(",").forEach((path) => {
          content += `Disallow: ${path.trim()}\n`;
        });
      }
      if (rule.allow) {
        rule.allow.split(",").forEach((path) => {
          content += `Allow: ${path.trim()}\n`;
        });
      }
      content += "\n";
    });

    if (sitemap) {
      content += `Sitemap: ${sitemap}`;
    }

    setOutput(content.trim());
  }, [rules, sitemap]);

  const addRule = () => {
    setRules([...rules, { agent: "*", allow: "", disallow: "" }]);
  };

  const removeRule = (index: number) => {
    const newRules = [...rules];
    newRules.splice(index, 1);
    setRules(newRules);
  };

  const updateRule = (index: number, field: keyof Rule, value: string) => {
    const newRules = [...rules];
    newRules[index][field] = value;
    setRules(newRules);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(output);
    message.success("Robots.txt copied to clipboard!");
  };

  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([output], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = "robots.txt";
    document.body.appendChild(element);
    element.click();
  };

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={12}>
        <Card
          className="glass-card"
          title="Configuration"
          bordered={false}
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={addRule}>
              Add User Agent
            </Button>
          }
        >
          <Form layout="vertical">
            <Form.Item label="Sitemap URL">
              <Input
                placeholder="https://example.com/sitemap.xml"
                value={sitemap}
                onChange={(e) => setSitemap(e.target.value)}
              />
            </Form.Item>

            <Divider orientationMargin="left">Crawl Rules</Divider>

            <div
              style={{ maxHeight: "500px", overflowY: "auto", paddingRight: 5 }}
            >
              {rules.map((rule, index) => (
                <Card
                  key={index}
                  type="inner"
                  size="small"
                  style={{
                    marginBottom: 16,
                    background: "rgba(255,255,255,0.02)",
                  }}
                  title={`Rule #${index + 1}`}
                  extra={
                    rules.length > 1 && (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeRule(index)}
                      />
                    )
                  }
                >
                  <Form.Item label="User Agent" style={{ marginBottom: 12 }}>
                    <Select
                      showSearch
                      value={rule.agent}
                      onChange={(val) => updateRule(index, "agent", val)}
                      optionFilterProp="children"
                    >
                      <Option value="*">All Robots (*)</Option>
                      <Option value="Googlebot">Googlebot</Option>
                      <Option value="Bingbot">Bingbot</Option>
                      <Option value="Yandex">Yandex</Option>
                      <Option value="DuckDuckBot">DuckDuckBot</Option>
                      <Option value="Baiduspider">Baiduspider</Option>
                      <Option value="Facebot">Facebook (Facebot)</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    label="Disallow Paths (comma separated)"
                    style={{ marginBottom: 12 }}
                  >
                    <Input
                      placeholder="/admin, /private"
                      value={rule.disallow}
                      onChange={(e) =>
                        updateRule(index, "disallow", e.target.value)
                      }
                    />
                  </Form.Item>
                  <Form.Item label="Allow Paths (comma separated)">
                    <Input
                      placeholder="/public, /images"
                      value={rule.allow}
                      onChange={(e) =>
                        updateRule(index, "allow", e.target.value)
                      }
                    />
                  </Form.Item>
                </Card>
              ))}
            </div>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={12}>
        <Card
          className="glass-card"
          title="Generated File"
          bordered={false}
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
          bodyStyle={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <TextArea
            className="code-editor"
            value={output}
            style={{
              flex: 1,
              resize: "none",
              borderRadius: 8,
              padding: 15,
              fontSize: 13,
              lineHeight: 1.5,
              minHeight: 300,
            }}
            readOnly
          />
          <Space
            style={{ marginTop: 20, width: "100%", justifyContent: "flex-end" }}
          >
            <Button icon={<CopyOutlined />} onClick={handleCopy}>
              Copy Code
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
            >
              Download .txt
            </Button>
          </Space>
        </Card>
      </Col>
    </Row>
  );
};

export default RobotsGen;
